// client/src/components/pages/TransactionAllocation/index.tsx (converted from .js, ST-00216)
import { useState, useEffect, useCallback, useRef, type ComponentType } from 'react';
import { FaRandom } from 'react-icons/fa';
import axios, { type AxiosResponse } from 'axios';
import { PageHeader } from '../../../types/commonComponents';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import FilterControlsJs from './FilterControls';
import TransactionTableJs from './TransactionTable';
import TransactionAllocationModalJs from './TransactionAllocationModal';
import { toApiDateString } from '../../../utils/dateUtils';
import type {
  AllocationErrorBody,
  AllocationGrouping,
  AllocationTransaction,
  ApplyAllocationsResponse,
  SuggestionsResponse,
} from '../../../types/clientPages/transactionAllocation';

/** What the page reads from a rejected request (axios error, or an abort). */
type RequestError = Error & {
  code?: string;
  response?: { status: number; data: AllocationErrorBody };
};

/**
 * A cancelled request (a newer one replaced it, the page unmounted, or Cancel). axios 1.x
 * rejects those with CanceledError, not AbortError, so testing the name alone showed
 * "Failed to load…" for every cancelled request.
 */
const isCancelled = (err: unknown): boolean => {
  if (axios.isCancel(err)) return true;
  const e = err as Partial<RequestError> | null | undefined;
  return e?.name === 'AbortError' || e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED';
};

// Typed views of the (still JavaScript) components this page renders.
const FilterControls = FilterControlsJs as unknown as ComponentType<{
  startDate: string;
  setStartDate: (date: string) => void;
  endDate: string;
  setEndDate: (date: string) => void;
  selectedGrouping: AllocationGrouping | null;
  setSelectedGrouping: (grouping: AllocationGrouping | null) => void;
  groupings: AllocationGrouping[];
  timingFilter: string;
  setTimingFilter: (value: string) => void;
  onApply: () => void;
  loading: boolean;
  loadingGroupings: boolean;
}>;
const TransactionTable = TransactionTableJs as unknown as ComponentType<{
  transactions: AllocationTransaction[];
  onSelectedAllocationsChange: (selected: AllocationTransaction[]) => void;
  selectedAllocations: AllocationTransaction[];
  onTransactionClick: (transaction: AllocationTransaction) => void;
}>;
const TransactionAllocationModal = TransactionAllocationModalJs as unknown as ComponentType<{
  isOpen: boolean;
  onClose: () => void;
  transaction: AllocationTransaction | null;
}>;

// Progress indicator component
const ProgressIndicator = ({ message, progress }: { message: string; progress?: number }) => (
  <div className="bg-surface p-6 rounded-lg shadow-md">
    <div className="flex items-center space-x-4">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-900">{message}</p>
        {progress !== undefined && (
          <div className="mt-2">
            <div className="bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">{progress}% complete</p>
          </div>
        )}
      </div>
    </div>
  </div>
);

const TransactionAllocation = () => {
  // Applying allocations amends the receipts: the server refuses it (403) without
  // transactions.allocate, so without it the page is read-only (viewer / user hold only
  // transactions.view).
  const { hasPermission, hasAnyAppRole } = useAuth();
  const canAllocate = hasPermission('transactions.allocate');
  // Allocation follows the amendment rule (ST-00221): administrators and global admins apply
  // directly; everyone else's allocation goes to Amendment Approvals. The server decides; this
  // only words the button and the confirmation.
  const appliesDirectly = hasAnyAppRole(['administrator', 'global-admin']);
  // State for date filters (same as Route Summary)
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    if (now.getDate() === 1) {
      return toApiDateString(new Date(now.getFullYear(), now.getMonth() - 1, 1));
    }
    const date = new Date();
    date.setDate(1);
    return toApiDateString(date);
  });

  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    if (now.getDate() === 1) {
      return toApiDateString(new Date(now.getFullYear(), now.getMonth(), 0));
    }
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return toApiDateString(date);
  });

  // State for grouping and timing filters
  const [selectedGrouping, setSelectedGrouping] = useState<AllocationGrouping | null>(null);
  const [groupings, setGroupings] = useState<AllocationGrouping[]>([]);
  const [timingFilter, setTimingFilter] = useState('No filter'); // 1-12 or 'No filter'

  // State for transaction data
  const [transactions, setTransactions] = useState<AllocationTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingGroupings, setLoadingGroupings] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  // Shown after Apply; lives outside the loading gate so it is actually seen (AMD-31)
  const [successMessage, setSuccessMessage] = useState('');
  // Shown when the allocation was sent for approval instead of applied
  const [approvalMessage, setApprovalMessage] = useState('');

  // State for selections
  const [selectedAllocations, setSelectedAllocations] = useState<AllocationTransaction[]>([]);

  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<AllocationTransaction | null>(
    null
  );

  // Refs for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  const groupingsAbortControllerRef = useRef<AbortController | null>(null);

  // Groupings load once, on mount. They do not depend on the dates; refetching them on every
  // date change swapped the filter panel (and the open date picker) for a skeleton, so the end
  // date could not be clicked (AMD-32).
  useEffect(() => {
    fetchGroupings();

    // Cleanup on unmount
    return () => {
      if (groupingsAbortControllerRef.current) {
        groupingsAbortControllerRef.current.abort();
      }
    };
    // Mount only (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (groupingsAbortControllerRef.current) {
        groupingsAbortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchGroupings = async () => {
    // Cancel previous request
    if (groupingsAbortControllerRef.current) {
      groupingsAbortControllerRef.current.abort();
    }

    // Create new abort controller
    groupingsAbortControllerRef.current = new AbortController();

    setLoadingGroupings(true);
    setError(null); // Clear any previous errors before fetching

    try {
      // No dates: the plain grouping list does not depend on them (no totals asked for)
      const response: AxiosResponse<AllocationGrouping[]> = await api.routeSummary.getGroupings(
        undefined,
        undefined,
        {
          signal: groupingsAbortControllerRef.current.signal,
        }
      );
      setGroupings(Array.isArray(response.data) ? response.data : []);
    } catch (caught) {
      if (!isCancelled(caught)) {
        console.error('Error fetching groupings:', caught);
        setError('Failed to load groupings');
      }
    } finally {
      setLoadingGroupings(false);
    }
  };

  const handleApplyFilters = async () => {
    if (!selectedGrouping) {
      setError('Please select a grouping');
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    setLoading(true);
    setError(null);
    setProgress(0);
    setProgressMessage('Fetching transaction data...');

    let progressInterval: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      // Improved progress simulation
      let currentProgress = 0;
      progressInterval = setInterval(() => {
        setProgress((prev) => {
          currentProgress = prev;
          if (prev < 85) {
            return prev + 15;
          } else if (prev < 95) {
            return prev + 2;
          } else if (prev < 98) {
            return prev + 0.5;
          }
          return prev;
        });
      }, 300);

      // Set a timeout to prevent infinite waiting
      timeoutId = setTimeout(() => {
        if (currentProgress < 100) {
          setProgressMessage('Request is taking longer than expected...');
        }
      }, 30000);

      const response: AxiosResponse<SuggestionsResponse> =
        await api.transactionAllocation.getTransactionsWithSuggestions(
          {
            groupingId: selectedGrouping.id,
            startDate,
            endDate,
            timingHours: timingFilter === 'No filter' ? null : parseInt(timingFilter),
          },
          {
            signal: abortControllerRef.current.signal,
          }
        );

      // Clear intervals and complete progress
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      setProgress(100);
      setProgressMessage('Data loaded successfully!');

      // Small delay to show completion
      setTimeout(() => {
        setTransactions(response.data.transactions || []);
        setProgress(0);
        setProgressMessage('');
      }, 500);
    } catch (caught) {
      // A rejection is not always an Error (nor has a message)
      const err = (caught ?? {}) as Partial<RequestError>;
      // Clean up intervals on error
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      if (!isCancelled(caught)) {
        console.error('Error fetching transactions:', caught);

        let errorMessage = 'Failed to fetch transaction data';
        const message = typeof err.message === 'string' ? err.message : '';
        if (err.code === 'ECONNABORTED' || message.includes('timeout')) {
          errorMessage =
            'Request timed out. Please try again or contact support if the issue persists.';
        } else if (err.response?.status === 404) {
          errorMessage = 'Grouping not found. Please refresh the page and try again.';
        } else if ((err.response?.status ?? 0) >= 500) {
          errorMessage = 'Server error occurred. Please try again in a few moments.';
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }

        setError(errorMessage);
        setTransactions([]);
      }

      setProgress(0);
      setProgressMessage('');
    } finally {
      setLoading(false);
    }
  };

  // Cancel data fetch
  const handleCancelFetch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLoading(false);
    setProgress(0);
    setProgressMessage('');
  };

  // Handle selected allocations change
  const handleSelectedAllocationsChange = useCallback((selectedData: AllocationTransaction[]) => {
    setSelectedAllocations(selectedData);
  }, []);

  // Handle transaction row click
  const handleTransactionClick = useCallback((transaction: AllocationTransaction) => {
    setSelectedTransaction(transaction);
    setModalOpen(true);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedTransaction(null);
  }, []);

  // Apply bulk allocations
  const handleApplyAllocations = async () => {
    if (!canAllocate) return;
    if (selectedAllocations.length === 0) {
      setError('Please select at least one allocation to apply');
      return;
    }

    if (
      !window.confirm(
        appliesDirectly
          ? `Are you sure you want to apply ${selectedAllocations.length} allocations? This will create new versions of the receipt transactions.`
          : `Send ${selectedAllocations.length} allocations for approval? Nothing changes until an approver applies them in Amendment Approvals.`
      )
    ) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccessMessage('');
    setApprovalMessage('');
    setProgressMessage(appliesDirectly ? 'Applying allocations...' : 'Sending for approval...');
    setProgress(0);

    try {
      const allocations = selectedAllocations.map((item) => ({
        receiptTicketUID: item.ReceiptUID,
        suggestedTicketNo: item.SuggestedTicketNo,
        dispatchTicketUID: item.DispatchUID,
      }));

      const response: AxiosResponse<ApplyAllocationsResponse | undefined> =
        await api.transactionAllocation.applyAllocations({
          allocations,
          amendmentNotes: `Bulk allocation applied: ${selectedAllocations.length} transactions allocated`,
        });

      console.log('Allocations applied:', response.data);
      const { message, summary, results, requiresApproval, approvalId }: ApplyAllocationsResponse =
        response.data || {};

      // Sent for approval: nothing was written, so there is nothing to reload. Say so plainly —
      // the receipts keep their delivery numbers until an approver applies the batch.
      if (requiresApproval) {
        setSelectedAllocations([]);
        setApprovalMessage(
          message ||
            `${summary?.submitted ?? selectedAllocations.length} allocation(s) submitted for approval${approvalId ? ` (approval #${approvalId})` : ''}.`
        );
        return;
      }

      // Refresh the data
      await handleApplyFilters();
      setSelectedAllocations([]);

      // Report what happened, including receipts that were not allocated (AMD-31)
      const failedResults = (results || []).filter((r) => !r.success);
      if ((summary?.failed ?? 0) > 0 || failedResults.length > 0) {
        const ticketNo = (uid: string) =>
          selectedAllocations.find((item) => item.ReceiptUID === uid)?.ReceiptTicketNo || uid;
        const failedList = failedResults
          .map((r) => `${ticketNo(r.receiptTicketUID)}${r.error ? ` (${r.error})` : ''}`)
          .join(', ');
        setError(
          `${message || 'Some allocations failed'}${failedList ? `. Not applied: ${failedList}` : ''}`
        );
      } else {
        setSuccessMessage(message || 'Allocations applied successfully');
      }
    } catch (caught) {
      const err = caught as RequestError;
      console.error('Error applying allocations:', err);
      // The server answers { error, message } (403/400) or { error, details } (500)
      const errorMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to apply allocations.';
      setError(errorMsg);
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaRandom}
        title="Transaction Allocation"
        subtitle="Allocate dispatch tickets to receipt transactions based on timing and vehicle registration"
      />

      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6">
        {/* Filter Controls: always mounted, so an open date picker survives (AMD-32); the
            grouping select is disabled while the groupings load */}
        <FilterControls
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          selectedGrouping={selectedGrouping}
          setSelectedGrouping={setSelectedGrouping}
          groupings={groupings}
          timingFilter={timingFilter}
          setTimingFilter={setTimingFilter}
          onApply={handleApplyFilters}
          loading={loading}
          loadingGroupings={loadingGroupings}
        />

        {/* Progress Indicator */}
        {loading && progressMessage && (
          <div className="mt-4">
            <ProgressIndicator message={progressMessage} progress={progress} />
            <div className="mt-2 flex justify-center">
              <button
                onClick={handleCancelFetch}
                className="px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {approvalMessage && (
          <div
            role="status"
            className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between"
          >
            <p className="text-amber-800">
              <span className="font-semibold">Sent for approval:</span> {approvalMessage}
            </p>
            <button
              onClick={() => setApprovalMessage('')}
              aria-label="Dismiss"
              className="text-amber-500 hover:text-amber-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between">
            <p className="text-emerald-700">{successMessage}</p>
            <button
              onClick={() => setSuccessMessage('')}
              aria-label="Dismiss"
              className="text-emerald-400 hover:text-emerald-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-600 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Bulk Action Button (needs transactions.allocate) */}
        {transactions.length > 0 && !loading && (
          <div className="mt-4">
            {canAllocate ? (
              <button
                onClick={handleApplyAllocations}
                disabled={selectedAllocations.length === 0 || loading}
                className="px-4 py-2 bg-primary-600 text-white rounded shadow hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {appliesDirectly
                  ? `Apply ${selectedAllocations.length} Selected Allocations`
                  : `Submit ${selectedAllocations.length} Selected Allocations for Approval`}
              </button>
            ) : (
              <p className="text-sm text-gray-500">
                You can review the suggested allocations here (read-only). Applying them needs
                allocation rights; ask an administrator.
              </p>
            )}
          </div>
        )}

        {/* Transaction Table */}
        {transactions.length > 0 && !loading && (
          <div className="mt-6">
            <TransactionTable
              transactions={transactions}
              onSelectedAllocationsChange={handleSelectedAllocationsChange}
              selectedAllocations={selectedAllocations}
              onTransactionClick={handleTransactionClick}
            />
          </div>
        )}

        {/* Empty State */}
        {!loading && transactions.length === 0 && !error && (
          <div className="mt-8 text-center">
            <div className="bg-surface p-8 rounded-lg shadow-md">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h3>
              <p className="text-gray-500">
                Select a grouping and apply filters to load transaction data.
              </p>
            </div>
          </div>
        )}

        {/* Transaction Details Modal */}
        <TransactionAllocationModal
          isOpen={modalOpen}
          onClose={handleModalClose}
          transaction={selectedTransaction}
        />
      </div>
    </div>
  );
};

export default TransactionAllocation;
